import React, { useState, useEffect } from 'react';
import {
	Button,
	Tag,
	Typography,
	Modal,
	message,
	Empty,
	Space,
	Table,
	Tooltip,
	Dropdown,
	MenuProps,
} from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	ReloadOutlined,
	FormOutlined,
	MessageOutlined,
	PhoneOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgentId } from '@/store/account/selector';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';
import { WORKFLOW_TYPES } from 'src/enums/EOutbound';
import { WorkflowModal } from './Workflows/WorkflowModal';

const { Title, Text } = Typography;
const { confirm } = Modal;

const formatDate = (dateString: string): string => {
	if (!dateString) {
		return '-';
	}
	return new Date(dateString).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const getWorkflowIcon = (type: string) => {
	switch (type?.toUpperCase()) {
		case 'INTAKE':
			return <FormOutlined className="text-blue-500" />;
		case 'SMS':
			return <MessageOutlined className="text-green-500" />;
		case 'TRANSFER':
			return <PhoneOutlined className="text-orange-500" />;
		default:
			return <FormOutlined className="text-gray-500" />;
	}
};

const getTypeColor = (type: string): string => {
	switch (type?.toUpperCase()) {
		case 'INTAKE':
			return 'blue';
		case 'SMS':
			return 'green';
		case 'TRANSFER':
			return 'orange';
		default:
			return 'gray';
	}
};

const OutboundWorkflows: React.FC = () => {
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const [workflows, setWorkflows] = useState<any[]>([]);
	const [refreshing, setRefreshing] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingWorkflow, setEditingWorkflow] = useState<any>(null);
	const [selectedWorkflowType, setSelectedWorkflowType] = useState<string>(WORKFLOW_TYPES.INTAKE);

	const { getWorkflowsMutation, deleteWorkflowMutation } = useOutbound();

	useEffect(() => {
		if (selectedAgentId) {
			handleRefresh();
		}
	}, [selectedAgentId]);

	const handleRefresh = async () => {
		if (!selectedAgentId) {
			return;
		}

		setRefreshing(true);
		try {
			getWorkflowsMutation.mutate(selectedAgentId, {
				onSuccess: (data) => {
					const outboundWorkflows =
						data.workflows?.filter((workflow: any) => workflow.createdFor === 'Outbound') || [];
					setWorkflows(outboundWorkflows);
				},
				onError: () => message.error('Failed to fetch workflows'),
			});
		} finally {
			setRefreshing(false);
		}
	};

	const handleDelete = (workflow: any) => {
		confirm({
			title: 'Delete Workflow',
			content: `Are you sure you want to delete "${workflow.name}"?`,
			okText: 'Delete',
			okType: 'danger',
			onOk: async () => {
				if (!selectedAgentId) {
					return;
				}
				try {
					await deleteWorkflowMutation.mutateAsync({
						clientId: selectedAgentId,
						workflowId: workflow.id,
					});
					await handleRefresh();
				} catch (_error) {
					message.error('Failed to delete workflow');
				}
			},
		});
	};

	const handleEdit = (workflow: any) => {
		setEditingWorkflow(workflow);
		setSelectedWorkflowType(workflow.type);
		setIsModalOpen(true);
	};

	const handleCreateWorkflow = (type: string) => {
		setSelectedWorkflowType(type);
		setEditingWorkflow(null);
		setIsModalOpen(true);
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditingWorkflow(null);
		handleRefresh();
	};

	const newWorkflowItems: MenuProps['items'] = [
		{
			key: WORKFLOW_TYPES.INTAKE,
			label: 'Intake Form',
			icon: <FormOutlined />,
			onClick: () => handleCreateWorkflow(WORKFLOW_TYPES.INTAKE),
		},
		{
			key: WORKFLOW_TYPES.SMS,
			label: 'SMS Workflow',
			icon: <MessageOutlined />,
			onClick: () => handleCreateWorkflow(WORKFLOW_TYPES.SMS),
		},
		{
			key: WORKFLOW_TYPES.TRANSFER,
			label: 'Call Transfer',
			icon: <PhoneOutlined />,
			onClick: () => handleCreateWorkflow(WORKFLOW_TYPES.TRANSFER),
		},
	];

	const columns = [
		{
			title: 'Workflow',
			key: 'workflow',
			width: 250,
			render: (record: any) => (
				<div className="flex items-center gap-3">
					{getWorkflowIcon(record.type)}
					<div>
						<Text strong>{record.name}</Text>
						<br />
						<Text type="secondary" className="text-xs">
							{record.scenario ? record.scenario.substring(0, 50) + '...' : 'No description'}
						</Text>
					</div>
				</div>
			),
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: 120,
			render: (type: string) => (
				<Tag color={getTypeColor(type)} icon={getWorkflowIcon(type)}>
					{type?.toUpperCase()}
				</Tag>
			),
		},
		{
			title: 'Status',
			dataIndex: 'enabled',
			key: 'enabled',
			width: 100,
			render: (enabled: boolean) => (
				<Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Enabled' : 'Disabled'}</Tag>
			),
		},
		{
			title: 'Questions',
			key: 'questions',
			width: 100,
			render: (record: any) => {
				const questionCount = record.data?.questions?.length || 0;
				return (
					<Text className="text-sm">
						{record.type?.toUpperCase() === 'INTAKE' ? `${questionCount} questions` : '-'}
					</Text>
				);
			},
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 120,
			render: (date: string) => <Text className="text-xs">{formatDate(date)}</Text>,
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			render: (record: any) => (
				<Space>
					<Tooltip title="Edit">
						<Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button
							type="link"
							danger
							icon={<DeleteOutlined />}
							loading={deleteWorkflowMutation.isPending}
							onClick={() => handleDelete(record)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	if (!selectedAgentId) {
		return (
			<div className="p-6 bg-white">
				<Empty
					description="Please select an agent to manage workflows"
					image={Empty.PRESENTED_IMAGE_SIMPLE}
				/>
			</div>
		);
	}

	return (
		<div className="p-6 bg-white">
			<div className="max-w-full">
				<div className="flex justify-between items-center mb-6">
					<div>
						<Title level={2} className="mb-1">
							Outbound Workflows
						</Title>
						<Text type="secondary">Manage your custom outbound workflows</Text>
					</div>
					<Space>
						<Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing}>
							Refresh
						</Button>
						<Dropdown menu={{ items: newWorkflowItems }} placement="bottomRight">
							<Button type="primary" icon={<PlusOutlined />}>
								New Workflow
							</Button>
						</Dropdown>
					</Space>
				</div>

				<Table
					columns={columns}
					dataSource={workflows}
					rowKey="id"
					loading={getWorkflowsMutation.isPending || refreshing}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} workflows`,
					}}
					locale={{
						emptyText: (
							<Empty description="No workflows found" image={Empty.PRESENTED_IMAGE_SIMPLE}>
								<Text type="secondary" className="block mb-4">
									Create custom workflows to automate your outbound processes
								</Text>
								<Dropdown menu={{ items: newWorkflowItems }} placement="top">
									<Button type="primary" icon={<PlusOutlined />}>
										Create Your First Workflow
									</Button>
								</Dropdown>
							</Empty>
						),
					}}
					size="middle"
					scroll={{ x: 800 }}
				/>

				<WorkflowModal
					open={isModalOpen}
					onClose={handleModalClose}
					type={selectedWorkflowType}
					editingWorkflow={editingWorkflow}
				/>
			</div>
		</div>
	);
};

export default OutboundWorkflows;
